import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enUS } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";

import Loading from "../ui/Loading";
import ScheduleFilters from "../schedule/ScheduleFilters";
import WorkRoleUserFilters from "../schedule/WorkRoleUserFilters";
import Modal from "../ui/Modal";
import { useScheduleData } from "../schedule/ScheduleDataContext";
import { patchScheduledResource, buildPatchForDates, updateSoftSchedule } from "../../api/client";
// Using the fixed modal implementation to avoid duplicate definition issues
import NewScheduledResourceModal from "../schedule/NewScheduledResourceModalFixed";
import NewSoftScheduleModal from "../schedule/NewSoftScheduleModal";
import SoftScheduleEditModal from "../schedule/SoftScheduleEditModal";

const locales = { "en-US": enUS };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales,
});

// Data is loaded via ScheduleDataContext; this component only renders and updates

const DnDCalendar = withDragAndDrop(Calendar);

// Lightweight debug logger: enabled in dev builds or when localStorage.rm.debug === '1'
function debugLog(...args) {
  try {
    const enabled = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production')
      || (typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('rm.debug') === '1');
    if (enabled && typeof console !== 'undefined' && console.log) console.log(...args);
  } catch (_) {
    // noop
  }
}

export default function ResourceCalendar() {
  const { token, tokenLoading, tokenError, loading, error, filteredAssignments, setAssignments, ensureDataForRange, rangeLoading, backgroundLoading, canWrite, activeDateStart, softSchedules, setSoftSchedules } = useScheduleData();
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [dayModal, setDayModal] = useState({ open: false, date: null, items: [] });
  const [editValues, setEditValues] = useState({ start: "", end: "" });
  const [saveMsg, setSaveMsg] = useState(null); // { type: 'saving'|'success'|'error', text }
  const [createOpen, setCreateOpen] = useState(false);

  // Soft schedule UI state (data lives in context)
  const [createSoftOpen, setCreateSoftOpen] = useState(false);
  const [selectedSoftEvent, setSelectedSoftEvent] = useState(null);
  const viewStartRef = useRef(null);

  useEffect(() => {
    // derive events from shared, filtered assignments
    const evt = (filteredAssignments || []).map(a => ({
      id: `${a.ticketId || "sr"}-${a.id || Math.random()}`,
      title: `${a.resourceName} • Ticket ${a.ticketId ?? "?"} • Project ${a.projectId ?? "?"}`,
      start: a.start,
      end: a.end,
      allDay: false,
      isSoft: false,
      meta: {
        resource: a.resourceName,
        ticketId: a.ticketId,
        projectId: a.projectId,
        ticketSummary: a.ticketSummary,
        srId: a.id,
        raw: a.raw,
      },
    }));
    setEvents(evt);
  }, [filteredAssignments]);

  // Derive soft schedule calendar events
  const softEvents = useMemo(() => {
    return (softSchedules || []).map(ss => {
      const start = new Date(ss.dateStart);
      const end = new Date(ss.dateEnd);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
      return {
        id: `soft-${ss.id}`,
        title: `[Soft] ${ss.projectInfo || ss.description || `Ticket ${ss.ticketId} • ${ss.memberIdentifier}`}`,
        start,
        end,
        allDay: false,
        isSoft: true,
        meta: { softData: ss },
      };
    }).filter(Boolean);
  }, [softSchedules]);

  const allEvents = useMemo(() => [...events, ...softEvents], [events, softEvents]);

  // Style soft events with a ghost/translucent appearance
  const eventPropGetter = useCallback((event) => {
    if (event.isSoft) {
      return {
        style: {
          backgroundColor: 'rgba(156, 39, 176, 0.18)',
          border: '1.5px dashed rgba(156, 39, 176, 0.65)',
          color: '#6a1b9a',
          opacity: 0.82,
          fontStyle: 'italic',
        },
      };
    }
    return {};
  }, []);

  const components = useMemo(() => ({
    // Place for custom renderers later if needed
  }), []);

  // Default view should center on "today" regardless of the earliest event date
  const defaultDate = new Date();

  function onRangeChange(range) {
    let start = null;
    let end = null;
    if (Array.isArray(range) && range.length) {
      start = range[0];
      end = range[range.length - 1];
    } else if (range && range.start && range.end) {
      start = range.start;
      end = range.end;
    }
    if (start && end) {
      // Extend end by a day to make it exclusive
      const endPlus = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1);
      ensureDataForRange(start, endPlus);
    }
  }

  if (tokenLoading || loading) return <Loading />;

  async function applyUpdate(event, nextStart, nextEnd) {
    const srId = event?.meta?.srId || event?.meta?.raw?.id;
    if (!srId) return;
    const original = { start: event.start, end: event.end };
    const ops = buildPatchForDates({ start: nextStart, end: nextEnd });
    // optimistic update
    setSavingId(srId);
    setSaveMsg({ type: "saving", text: "Saving..." });
    setAssignments(prev => prev.map(a => a.id === srId ? { ...a, start: nextStart, end: nextEnd } : a));
    try {
  await patchScheduledResource(srId, ops, token);
  setSaveMsg({ type: "success", text: "Saved" });
  setTimeout(() => setSaveMsg(null), 4000);
    } catch (e) {
      // revert
      setAssignments(prev => prev.map(a => a.id === srId ? { ...a, ...original } : a));
      setSaveMsg({ type: "error", text: e?.message || "Failed to save" });
    } finally {
      setSavingId(null);
    }
  }

  const onEventDrop = async ({ event, start, end }) => {
    // Handle soft schedule drag separately
    if (event.isSoft) {
      const softId = event.meta.softData.id;
      const MS_PER_DAY = 24 * 60 * 60 * 1000;
      const origStart = event.start;
      const dayStartOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const deltaDays = Math.round((dayStartOf(start).getTime() - dayStartOf(origStart).getTime()) / MS_PER_DAY);
      if (deltaDays === 0) return;
      const addDays = (d, days) => {
        const dt = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days));
        const pad = (n) => String(n).padStart(2, '0');
        return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}T00:00:00Z`;
      };
      const origData = event.meta.softData;
      const newDs = addDays(new Date(origData.dateStart), deltaDays);
      const newDe = addDays(new Date(origData.dateEnd), deltaDays);
      // Optimistic update
      setSoftSchedules(prev =>
        prev.map(ss => ss.id === softId ? { ...ss, dateStart: newDs, dateEnd: newDe } : ss)
      );
      try {
        await updateSoftSchedule(softId, { dateStart: newDs, dateEnd: newDe }, token);
      } catch (e) {
        // Revert on failure
        setSoftSchedules(prev =>
          prev.map(ss => ss.id === softId ? { ...ss, dateStart: origData.dateStart, dateEnd: origData.dateEnd } : ss)
        );
        setSaveMsg({ type: 'error', text: `Failed to move soft schedule: ${e?.message}` });
      }
      return;
    }

    // Day-based delta: move by whole days relative to original start; keep time-of-day and duration
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const origStart = event.start;
    const origEnd = event.end;
    if (!origStart || !origEnd) return;
    const dayStartOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const startDay = dayStartOf(start);
    const origStartDay = dayStartOf(origStart);
    const deltaDays = Math.round((startDay.getTime() - origStartDay.getTime()) / MS_PER_DAY);
  debugLog('[CalendarDrag]', 'deltaDays=', deltaDays, 'origStart=', origStart.toISOString(), 'dropStart=', start.toISOString());
    if (deltaDays === 0) return;
    const addDaysKeepTime = (d, days) => new Date(
      d.getFullYear(), d.getMonth(), d.getDate() + days,
      d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds()
    );
    const newStart = addDaysKeepTime(origStart, deltaDays);
    const newEnd = addDaysKeepTime(origEnd, deltaDays);
  debugLog('[CalendarDrag] Applying shift.', 'newStart=', newStart.toISOString(), 'newEnd=', newEnd.toISOString());
    await applyUpdate(event, newStart, newEnd);
  };

  const onEventResize = async ({ event, start, end }) => {
    await applyUpdate(event, start, end);
  };
  const Banner = () => (
    (rangeLoading || backgroundLoading) ? (
      <div style={{ fontSize: 12, marginBottom: 8, display: 'inline-flex', alignItems: 'center', gap: 6, color: '#1976d2' }}>
        <span style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid #90caf9', borderTopColor: 'transparent', animation: 'rt-spin 0.7s linear infinite' }} />
        Loading in background…
      </div>
    ) : null
  );

  // Helpers for editing from popup
  function toInputValue(d) {
    if (!d) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`; // local time
  }

  function fromInputValue(v) {
    if (!v) return null;
    // new Date('YYYY-MM-DDTHH:mm') is local; keep it
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }

  const onSelectEvent = (e) => {
    // Soft schedule events are handled via double-click; skip regular popup for them
    if (e.isSoft) return;
    // Ensure only one modal is open at a time
    setDayModal({ open: false, date: null, items: [] });
    setSaveMsg(null);
    setSelected(e);
    if (canWrite) setEditValues({ start: toInputValue(e.start), end: toInputValue(e.end) });
  };

  const onDoubleClickEvent = (e) => {
    if (e.isSoft) {
      setSelected(null);
      setDayModal({ open: false, date: null, items: [] });
      setSelectedSoftEvent(e);
    }
  };

  const onSaveEdit = async () => {
    if (!selected) return;
    const ns = fromInputValue(editValues.start);
    const ne = fromInputValue(editValues.end);
    if (!ns || !ne) {
      setSaveMsg({ type: "error", text: "Please provide valid start and end date/times" });
      return;
    }
    if (ne <= ns) {
      setSaveMsg({ type: "error", text: "End must be after start" });
      return;
    }
    await applyUpdate(selected, ns, ne);
    // refresh selected with new values
    setSelected((prev) => (prev ? { ...prev, start: ns, end: ne } : prev));
  };

  // Month "+ more" replacement: open our own day modal listing all events
  const onShowMore = (dayEvents, date) => {
    // Close event editor if open
    setSelected(null);
    setDayModal({ open: true, date, items: dayEvents });
  };

  const onSelectSlot = ({ start }) => {
    // Open day modal for selected day
    const dayStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const dayEnd = new Date(dayStart.getFullYear(), dayStart.getMonth(), dayStart.getDate() + 1);
    const items = events.filter(e => e.start >= dayStart && e.start < dayEnd);
    setSelected(null);
    setDayModal({ open: true, date: dayStart, items });
  };

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        {canWrite && <button onClick={() => setCreateOpen(true)} aria-label="Add new resource assignment">Add Resource</button>}
        {canWrite && (
          <button
            onClick={() => setCreateSoftOpen(true)}
            aria-label="Add soft schedule"
            style={{ background: 'rgba(156,39,176,0.12)', border: '1.5px dashed rgba(156,39,176,0.65)', color: '#6a1b9a', borderRadius: 4, padding: '4px 10px', cursor: 'pointer' }}
          >
            Add Soft Schedule
          </button>
        )}
        {!canWrite && (
          <span style={{ fontSize: 12, padding: '2px 6px', border: '1px solid #90caf9', borderRadius: 4, color: '#0d47a1', background: '#e3f2fd' }}>
            Read-only
          </span>
        )}
      </div>
      {tokenError && (
        <div style={{ color: "#b00020", marginBottom: 12 }}>
          Failed to acquire token: {tokenError.message}
        </div>
      )}
      {error && (
        <div style={{ color: "#b00020", marginBottom: 12 }}>
          Failed to load calendar: {error.message}
        </div>
      )}
  <ScheduleFilters />
      <div style={{ marginBottom: 8 }}>Assignments: {events.length}{softEvents.length > 0 ? ` • Soft: ${softEvents.length}` : ''}</div>
      {events.length === 0 && !loading && !rangeLoading && (
        <div style={{ textAlign: 'center', padding: '40px 16px', color: '#90caf9', opacity: 0.85 }}>
          <div style={{ fontSize: 18, marginBottom: 8 }}>No assignments to display</div>
          <div style={{ fontSize: 13 }}>Try adjusting your filters, loading a different date range, or adding a new resource.</div>
        </div>
      )}
      <DndProvider backend={HTML5Backend}>
        {/* Calendar container with optional loading overlay */}
        <div style={{ position: 'relative' }}>
          {rangeLoading && (
            <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 10, display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.9)', border: '1px solid #1976d2', color: '#0d47a1', borderRadius: 4, padding: '4px 8px' }}>
              <span className="rt-spinner" style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid #90caf9', borderTopColor: 'transparent', animation: 'rt-spin 0.7s linear infinite' }} />
              Loading month…
            </div>
          )}
          {(() => {
            const Cal = canWrite ? DnDCalendar : Calendar;
            return (
              <Cal
                localizer={localizer}
                events={allEvents}
                startAccessor="start"
                endAccessor="end"
                style={{ height: "75vh" }}
                popup={false}
                selectable
                defaultDate={defaultDate}
                resizable={!!canWrite}
                onEventDrop={canWrite ? onEventDrop : undefined}
                onEventResize={canWrite ? onEventResize : undefined}
                onSelectEvent={onSelectEvent}
                onDoubleClickEvent={onDoubleClickEvent}
                onSelectSlot={onSelectSlot}
                onShowMore={onShowMore}
                onRangeChange={onRangeChange}
                components={components}
                eventPropGetter={eventPropGetter}
              />
            );
          })()}
        </div>
      </DndProvider>

      {selected && (
        <Modal onClose={() => { setSelected(null); setSaveMsg(null); }} aria-label="Assignment Details">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0 }}>Assignment Details{!canWrite ? ' (Read-only)' : ''}</h3>
            <button onClick={() => { setSelected(null); setSaveMsg(null); }}>Close</button>
          </div>
          <div style={{ marginTop: 12 }}>
            <div><strong>Resource:</strong> {selected.meta?.resource}</div>
            <div><strong>Ticket ID:</strong> {selected.meta?.ticketId ?? "?"}</div>
            <div><strong>Project ID:</strong> {selected.meta?.projectId ?? "?"}</div>
            {selected.meta?.ticketSummary && (
              <div><strong>Ticket Summary:</strong> {selected.meta.ticketSummary}</div>
            )}
            {canWrite ? (
              <>
                <div style={{ marginTop: 8 }}>
                  <label>
                    Start:
                    <input
                      type="datetime-local"
                      value={editValues.start}
                      onChange={(e) => setEditValues((v) => ({ ...v, start: e.target.value }))}
                      style={{ marginLeft: 8 }}
                    />
                  </label>
                </div>
                <div style={{ marginTop: 8 }}>
                  <label>
                    End:
                    <input
                      type="datetime-local"
                      value={editValues.end}
                      onChange={(e) => setEditValues((v) => ({ ...v, end: e.target.value }))}
                      style={{ marginLeft: 8 }}
                    />
                  </label>
                </div>
                <div style={{ marginTop: 12 }}>
                  <button onClick={onSaveEdit} disabled={!!savingId}>{savingId ? "Saving..." : "Save"}</button>
                  <div style={{ marginTop: 8, minHeight: 20 }}>
                    {saveMsg?.type === "saving" && <span>Saving...</span>}
                    {saveMsg?.type === "success" && <span style={{ color: "#2e7d32" }}>Saved</span>}
                    {saveMsg?.type === "error" && (
                      <span style={{ color: "#b00020" }}>Save failed: {saveMsg.text}</span>
                    )}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </Modal>
      )}

      {dayModal.open && (
        <Modal onClose={() => setDayModal({ open: false, date: null, items: [] })} aria-label="Day events">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0 }}>Events for {dayModal.date?.toDateString()}</h3>
            <button onClick={() => setDayModal({ open: false, date: null, items: [] })}>Close</button>
          </div>
          <div style={{ marginTop: 12, maxHeight: "60vh", overflow: "auto" }}>
            {dayModal.items.length === 0 && <div>No events</div>}
            {dayModal.items.map((ev) => (
              <div key={ev.id} style={{ padding: "8px 0", borderBottom: "1px solid rgba(0,0,0,0.1)" }}>
                <div style={{ fontWeight: 600 }}>{ev.title}</div>
                <div style={{ fontSize: 12 }}>
                  {ev.start.toLocaleString()} — {ev.end.toLocaleString()}
                </div>
                {ev.meta?.ticketSummary && (
                  <div style={{ fontSize: 12, color: "#333", marginTop: 4 }}>
                    {ev.meta.ticketSummary}
                  </div>
                )}
                {canWrite && (
                  <div style={{ marginTop: 6 }}>
                    <button onClick={() => { setDayModal({ open: false, date: null, items: [] }); setSaveMsg(null); setSelected(ev); setEditValues({ start: toInputValue(ev.start), end: toInputValue(ev.end) }); }}>Edit</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Modal>
      )}

      <NewScheduledResourceModal open={createOpen} onClose={() => setCreateOpen(false)} />

      <NewSoftScheduleModal
        open={createSoftOpen}
        onClose={() => setCreateSoftOpen(false)}
        onCreated={(newSoft) => {
          setSoftSchedules(prev => [...prev, newSoft]);
        }}
      />

      <SoftScheduleEditModal
        softEvent={selectedSoftEvent}
        onClose={() => setSelectedSoftEvent(null)}
        onUpdated={(updatedData) => {
          setSoftSchedules(prev =>
            prev.map(ss => ss.id === updatedData.id ? { ...ss, ...updatedData } : ss)
          );
        }}
        onDeleted={(id) => {
          setSoftSchedules(prev => prev.filter(ss => ss.id !== id));
          setSelectedSoftEvent(null);
        }}
      />
    </div>
  );
}
